/**
 * Formatting for analytics numbers: money in the ad account's currency,
 * compact counts, rates, and platform / result-type names.
 */
import type { AdPlatform, ResultType, ResultsByType } from '@/types/analytics';
import { CURRENCY_CONFIG } from '@/utils/currency';

export const PLATFORM_ORDER: AdPlatform[] = ['meta', 'google', 'pinterest', 'twitter'];

export const PLATFORM_LABEL: Record<AdPlatform, string> = {
  meta: 'Meta',
  google: 'Google',
  pinterest: 'Pinterest',
  twitter: 'X',
};

/**
 * Fixed per platform (color follows the platform, never its rank). Order and
 * steps validated for adjacent-pair colour-blind separation on the app's
 * light (#FFFFFF) and dark (#1A1D26) cards.
 */
export const PLATFORM_COLOR: Record<'light' | 'dark', Record<AdPlatform, string>> = {
  light: { meta: '#2a78d6', google: '#1baf7a', pinterest: '#4a3aa7', twitter: '#eda100' },
  dark: { meta: '#3987e5', google: '#199e70', pinterest: '#9085e9', twitter: '#c98500' },
};

export function platformLabel(platform: string): string {
  return PLATFORM_LABEL[platform as AdPlatform] ?? platform;
}

const META_SOURCE: Record<string, string> = {
  fb: 'Facebook', facebook: 'Facebook', ig: 'Instagram', instagram: 'Instagram',
  messenger: 'Messenger', an: 'Audience Network', audience_network: 'Audience Network',
};

/** 'Meta · Instagram' when a Meta lead says where it was filled; otherwise the platform. */
export function leadSourceLabel(platform: string, sourcePlatform?: string | null): string {
  const source = sourcePlatform ? META_SOURCE[sourcePlatform.toLowerCase()] : undefined;
  return platform === 'meta' && source ? `Meta · ${source}` : platformLabel(platform);
}

/** Money in the account currency; plain number when the currency is unknown. */
export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined,
  opts: { compact?: boolean } = {},
): string {
  if (amount === null || amount === undefined) return '—';
  const digits = Math.abs(amount) >= 100 || opts.compact ? 0 : 2;
  const options: Intl.NumberFormatOptions = {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
    ...(opts.compact && Math.abs(amount) >= 10_000 ? { notation: 'compact', maximumFractionDigits: 1, minimumFractionDigits: 0 } : {}),
  };
  if (currency) {
    try {
      // The currency's home locale gives "$1,972" / "₹1,972" rather than "US$1,972".
      const locale = CURRENCY_CONFIG[currency.toUpperCase()]?.locale;
      return new Intl.NumberFormat(locale, { style: 'currency', currency, ...options }).format(amount);
    } catch {
      // Unknown currency code: fall through to a plain number with the code.
      return `${new Intl.NumberFormat(undefined, options).format(amount)} ${currency}`;
    }
  }
  return new Intl.NumberFormat(undefined, options).format(amount);
}

export function formatCount(value: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (value === null || value === undefined) return '—';
  if (opts.compact && Math.abs(value) >= 10_000) {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  }
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

export function formatPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(digits)}%`;
}

const RESULT_NOUN: Record<ResultType, [string, string]> = {
  leads: ['lead', 'leads'],
  purchases: ['purchase', 'purchases'],
  registrations: ['sign-up', 'sign-ups'],
  conversions: ['conversion', 'conversions'],
  clicks: ['click', 'clicks'],
  impressions: ['impression', 'impressions'],
};

export function resultNoun(type: ResultType | string, count = 2): string {
  const nouns = RESULT_NOUN[type as ResultType] ?? ['result', 'results'];
  return count === 1 ? nouns[0] : nouns[1];
}

/** "Cost per lead", or "Cost per 1K impressions" for awareness campaigns. */
export function costPerLabel(type: ResultType | string): string {
  if (type === 'impressions') return 'Cost per 1K impressions';
  return `Cost per ${resultNoun(type, 1)}`;
}

/** "42 leads · 6 purchases" — only the kinds that happened. */
export function resultsBreakdown(results: ResultsByType | undefined | null): string {
  if (!results) return '';
  const parts: string[] = [];
  (['leads', 'purchases', 'registrations'] as const).forEach((key) => {
    if (results[key] > 0) parts.push(`${formatCount(results[key])} ${resultNoun(key, results[key])}`);
  });
  if (results.other > 0) parts.push(`${formatCount(results.other)} other`);
  return parts.join(' · ');
}

/** "+12.5%" / "−3.0%"; null when there is nothing to compare. */
export function formatTrend(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(1)}%`;
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}
